import { FoodsIcon } from "@foodpilot/foods";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useNavigate, useLocation } from "react-router";
import { useIngredientDelete } from "src/api/client-api/ingredients/useDeleteIngredient";
import { useCloneIngredient } from "src/api/client-api/ingredients/useCloneIngredient";
import { ingredientGridKey, IngredientList } from "src/api/client-api/ingredients/useGetAllIngredients";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { CanUserCallback, CREATE, INGREDIENT } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import type { ComponentAppearance } from "src/components/appearance";

type MenuItemsOptions = {
    appearance?: ComponentAppearance;
    companyIds: {
        "@id": IRI;
        id: number;
    };
    canUser: CanUserCallback;
};

export const useIngredientMenuItems = (options: MenuItemsOptions) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { appearance = "legacy", canUser, companyIds } = options;

    const cloneIngredient = useCloneIngredient();
    const deleteIngredient = useIngredientDelete();
    const queryClient = useQueryClient();

    const menuItemsAcc: MenuItemType<IngredientList>[] = [];

    menuItemsAcc.push({
        icon: <FoodsIcon icon="scoresIndicators" size={3} />,
        label: t("scores_indicateurs_cles"),
        onClick: (entity) => {
            navigate(URLHandler.ingredient.scores(companyIds.id, entity.id));
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });

    menuItemsAcc.push({
        icon: <FoodsIcon icon="questionnaires" size={3} />,
        label: t("questionnaires"),
        onClick: (entity) => {
            navigate(URLHandler.ingredient.view(companyIds.id, entity.id));
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });

    menuItemsAcc.push({
        icon: <FoodsIcon icon="informations" size={3} />,
        label: t("informations"),
        onClick: (entity) => {
            navigate(URLHandler.ingredient.information(companyIds.id, entity.id));
        },
        shouldShow: (entity) => {
            return entity?.canView ?? false;
        },
    });

    menuItemsAcc.push({
        icon: appearance === "product-v2" ? <FoodsIcon icon="duplicate" size={3} /> : <ContentCopy />,
        label: t("dupliquer"),
        onClick: (entity) => {
            const options = {
                ingredientId: entity.id,
                suffix: t("copy"),
            };
            cloneIngredient.mutate(options, {
                onSuccess: () => {
                    queryClient.invalidateQueries([ingredientGridKey]);
                    // setRowMenuAnchor(null);
                },
            });
        },
        shouldShow: (_entity) => {
            return canUser(CREATE, INGREDIENT);
        },
    });

    menuItemsAcc.push({
        icon: appearance === "product-v2" ? <FoodsIcon icon="edit" size={3} /> : <Edit />,
        label: t("modifier"),
        onClick: (entity) => {
            const currentUrl = location.pathname + location.search;
            const editUrl = URLHandler.ingredient.edit(companyIds.id, entity.id, 0);
            navigate(`${editUrl}?returnUrl=${encodeURIComponent(currentUrl)}`);
        },
        shouldShow: (entity) => {
            return entity?.canModify ?? false;
        },
    });

    menuItemsAcc.push({
        icon: appearance === "product-v2" ? <FoodsIcon icon="delete" size={3} /> : <Delete />,
        label: t("supprimer"),
        onClick: (entity) => {
            deleteIngredient.mutate(entity.id, {
                onSuccess: () => {
                    queryClient.invalidateQueries([ingredientGridKey]);
                },
            });
        },
        shouldShow: (entity) => {
            return entity?.canDelete ?? false;
        },
    });

    return menuItemsAcc;
};
